import type { MockCity, MockDistrict, MockPropertyRecord } from '@app/mock-api/mock-api-shared-models';

export interface PublicCatalogListItem {
  readonly bedroomCount: null | number;
  readonly city: { name: string; slug: string; state: string };
  readonly district: { name: string; slug: string } | null;
  readonly parkingSpaceCount: null | number;
  readonly primaryImageUrl: null | string;
  readonly price: null | number;
  readonly propertyType: null | number;
  readonly publicSlug: string;
  readonly title: string;
  readonly updatedAtUtc: string;
}

export interface PublicCatalogDetail {
  readonly bathroomCount: null | number;
  readonly bedroomCount: null | number;
  readonly city: { name: string; slug: string; state: string };
  readonly createdAtUtc: string;
  readonly description: null | string;
  readonly district: null | string;
  readonly images: {
    isPrimary: boolean;
    sortOrder: number;
    url: string;
    urls: { detail: string; listing: string; thumbnail: string; zoom: string };
  }[];
  readonly parkingSpaceCount: null | number;
  readonly postalCode: null | string;
  readonly price: null | number;
  readonly propertyType: null | number;
  readonly publicSlug: string;
  readonly referenceCode: string;
  readonly street: null | string;
  readonly title: null | string;
  readonly totalAreaSqm: null | number;
  readonly updatedAtUtc: string;
}

function formatCepDigits(digits: string): null | string {
  const d = digits.replace(/\D/g, '');

  if (d.length === 0)
    return null;

  if (d.length === 8)
    return `${d.slice(0, 5)}-${d.slice(5)}`;

  return d;
}

function formatStreet(streetLine: string, streetNumber: string): null | string {
  const parts = [streetLine.trim(), streetNumber.trim()].filter((x) => x.length > 0);

  if (parts.length === 0)
    return null;

  return parts.join(', ');
}

export function toPublicCatalogListItem(
  p: MockPropertyRecord,
  city: MockCity,
  district: MockDistrict | null,
): PublicCatalogListItem {
  const sorted = [...p.images].sort((a, b) => {
    const o = a.sortOrder - b.sortOrder;
    return o !== 0 ? o : a.id.localeCompare(b.id, 'en');
  });
  const primary = sorted.find((i) => i.isPrimary) ?? sorted[0];

  return {
    bedroomCount: p.bedroomCount,
    city: { name: city.name, slug: city.slug, state: city.state },
    district: district ? { name: district.name, slug: district.slug } : null,
    parkingSpaceCount: p.parkingSpaceCount,
    primaryImageUrl: primary?.urls.listing ?? primary?.url ?? null,
    price: p.salePrice,
    propertyType: p.propertyType,
    publicSlug: p.publicSlug,
    title: p.title ?? '',
    updatedAtUtc: p.updatedAtUtc,
  };
}

export function toPublicCatalogDetail(
  p: MockPropertyRecord,
  city: MockCity,
  district: MockDistrict | null,
  createdAtUtc: string,
): PublicCatalogDetail {
  const images = [...p.images]
    .sort((a, b) => {
      const o = a.sortOrder - b.sortOrder;
      return o !== 0 ? o : a.id.localeCompare(b.id, 'en');
    })
    .map((img) => ({
      isPrimary: img.isPrimary,
      sortOrder: img.sortOrder,
      url: img.url,
      urls: img.urls,
    }));

  return {
    bathroomCount: p.bathroomCount,
    bedroomCount: p.bedroomCount,
    city: { name: city.name, slug: city.slug, state: city.state },
    createdAtUtc,
    description: p.description,
    district: district?.name ?? null,
    images,
    parkingSpaceCount: p.parkingSpaceCount,
    postalCode: formatCepDigits(p.postalCode),
    price: p.salePrice,
    propertyType: p.propertyType,
    publicSlug: p.publicSlug,
    referenceCode: p.referenceCode,
    street: formatStreet(p.streetLine, p.streetNumber),
    title: p.title,
    totalAreaSqm: p.totalAreaSqm,
    updatedAtUtc: p.updatedAtUtc,
  };
}
